<template>
  <BaseModule
    v-bind="$props"
    ref="menu"
    data-module="menu"
    class="menu"
    :class="{ transparent: opacity < 1 || useGradient }"
    :style="{
      '--text-color': textColor,
      '--mobile-text-color': mobileTextColor,
      '--button-size-desk': `${buttonSizeDesk}px`,
      '--button-size-mobile': `${buttonSizeMobile}px`,
      '--button-color': buttonColor,
      '--button-text-color': buttonTextColor,
    }"
  >
    <header
      class="header"
      :style="{ background: bgColor }"
    >
      <div
        class="header__content"
        :class="[
          { 'links-center': linksPosition === 'Centro' },
          { 'links-left': linksPosition === 'Esquerda' },
        ]"
      >
        <div class="logo-wrapper">
          <EditorLink :model="useLogoLink">
            <img
              v-if="logoImg"
              :src="logoImg"
              :alt="logoAltText"
            />
            <component
              :is="titleTag"
              v-else
            >
              {{ logoAltText }}
            </component>
          </EditorLink>
        </div>

        <nav class="nav-desk">
          <ul>
            <li
              v-for="menuLink in menuLinks"
              :key="menuLink.id"
              class="nav-item"
              @mouseenter="!isMobile && openSubmenu(menuLink.id)"
              @mouseleave="!isMobile && closeSubmenu()"
            >
              <button
                v-if="itemHasSubmenu(menuLink)"
                class="nav-link"
                :class="{ active: openedSubmenu === menuLink.id }"
                @click="toggleSubmenu(menuLink.id)"
              >
                {{ menuLink.linkText }}
                <IconArrow />
              </button>
              <EditorLink
                v-else
                class="nav-link"
                :model="menuLink.linkUrl"
                @anchoring="closeMobileMenu"
              >
                {{ menuLink.linkText }}
              </EditorLink>
              <transition name="slide-down">
                <ul
                  v-show="openedSubmenu === menuLink.id"
                  v-if="itemHasSubmenu(menuLink)"
                  @click.self.stop
                >
                  <li
                    v-for="subLink in menuLink.menuLinks"
                    :key="subLink.id"
                  >
                    <EditorLink
                      class="nav-sublink"
                      :model="subLink.linkUrl"
                      @blur.native="closeSubmenu"
                      @anchoring="closeMobileMenu"
                    >
                      {{ subLink.linkText }}
                    </EditorLink>
                  </li>
                </ul>
              </transition>
            </li>
          </ul>

          <button
            v-if="isMobile"
            @click="closeMobileMenu()"
          >
            <IconClose
              :color="mobileTextColor"
              title="Fechar"
            />
          </button>
        </nav>

        <div
          v-if="buttons.length"
          class="header__buttons"
        >
          <EditorLink
            v-for="(button, index) in buttons"
            :key="index"
            :model="button.buttonUrl"
            class="cta"
            :class="{
              vazado: !button.buttonFeatured,
            }"
          >
            {{ button.buttonText }}
          </EditorLink>
        </div>

        <button
          v-if="showButtonMenuMobile"
          class="hamburger-btn"
          @click="openMobileMenu()"
        >
          <IconHamburger
            :color="textColor"
            title="Menu"
          />
        </button>
      </div>
    </header>
    <nav
      class="nav-mobile"
      :is-open="isMobileMenuOpen"
    >
      <ul>
        <li
          v-for="menuLink in menuLinks"
          :key="menuLink.id"
          class="nav-item"
          @mouseenter="!isMobile && openSubmenu(menuLink.id)"
          @mouseleave="!isMobile && closeSubmenu()"
        >
          <button
            v-if="itemHasSubmenu(menuLink)"
            class="nav-link"
            :class="{ active: openedSubmenu === menuLink.id }"
            @click="toggleSubmenu(menuLink.id)"
          >
            {{ menuLink.linkText }}
            <IconArrow />
          </button>
          <EditorLink
            v-else
            class="nav-link"
            :model="menuLink.linkUrl"
            @anchoring="closeMobileMenu"
          >
            {{ menuLink.linkText }}
          </EditorLink>
          <transition name="slide-down">
            <ul
              v-show="openedSubmenu === menuLink.id"
              v-if="itemHasSubmenu(menuLink)"
              @click.self.stop
            >
              <li
                v-for="subLink in menuLink.menuLinks"
                :key="subLink.id"
              >
                <EditorLink
                  class="nav-sublink"
                  :model="subLink.linkUrl"
                  @blur.native="closeSubmenu"
                  @anchoring="closeMobileMenu"
                >
                  {{ subLink.linkText }}
                </EditorLink>
              </li>
            </ul>
          </transition>
        </li>
      </ul>

      <button
        v-if="isMobile"
        @click="closeMobileMenu()"
      >
        <IconClose
          :color="mobileTextColor"
          title="Fechar"
        />
      </button>
    </nav>
  </BaseModule>
</template>

<script>
import { mapGetters } from 'vuex';
import debounce from 'lodash/debounce';

import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import IconHamburger from '@/components/icons/IconHamburger.vue';
import IconClose from '@/components/icons/IconClose.vue';
import IconArrow from '@/components/icons/IconArrow.vue';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

const tinycolor = require('tinycolor2');

export default {
  name: 'Menu',
  components: {
    BaseModule,
    EditorLink,
    IconHamburger,
    IconClose,
    IconArrow,
  },
  mixins: [showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    logoAltText: {
      type: String,
      default: '',
    },
    logoImg: {
      type: String,
      default: '',
    },
    hasLogoLink: {
      type: Boolean,
      default: false,
    },
    logoUrl: {
      type: Object,
      default: undefined,
    },
    buttons: {
      type: Array,
      default: () => [],
    },
    menuLinks: {
      type: Array,
      default: () => [],
    },
    linksPosition: {
      type: String,
      default: 'Direita',
    },
    backgroundColor: {
      type: String,
      default: '#000000',
    },
    buttonColor: {
      type: String,
      default: '',
    },
    opacity: {
      type: Number,
      default: 1,
    },
    useGradient: {
      type: Boolean,
      default: false,
    },
    textColor: {
      type: String,
      default: '#ffffff',
    },
    buttonTextColor: {
      type: String,
      default: '#ffffff',
    },
    paddingTop: {
      type: Number,
      default: 15,
    },
    contentSizeDesk: {
      type: Number,
      default: 16,
    },
    contentSizeMobile: {
      type: Number,
      default: 18,
    },
    buttonSizeDesk: {
      type: Number,
      default: 16,
    },
    buttonSizeMobile: {
      type: Number,
      default: 16,
    },
    paddingBottom: {
      type: Number,
      default: 15,
    },
  },
  data() {
    return {
      isMobileMenuOpen: false,
      isMobile: window.innerWidth < 992,
      pages: [],
      openedSubmenu: null,
    };
  },
  computed: {
    useLogoLink() {
      return this.hasLogoLink ? this.logoUrl : undefined;
    },
    showButtonMenuMobile() {
      return this.menuLinks?.length > 0;
    },
    bgColor() {
      const colorWithOpacity = this.applyOpacity(
        this.backgroundColor,
        this.opacity,
      );

      if (this.useGradient) {
        return `linear-gradient(to bottom, ${colorWithOpacity}, transparent)`;
      }
      return colorWithOpacity;
    },
    mobileTextColor() {
      return tinycolor(this.settings.primaryColor).isDark()
        ? '#FFFFFF'
        : '#000000';
    },
    ...mapGetters(['settings']),
  },
  mounted() {
    window.addEventListener('resize', debounce(this.checkResolution, 150));
  },
  methods: {
    checkResolution() {
      if (this.isMobile !== window.innerWidth < 992) {
        this.isMobile = window.innerWidth < 992;
      }
      if (this.isMobileMenuOpen && !this.isMobile) {
        this.isMobileMenuOpen = false;
      }
    },
    openSubmenu(id) {
      this.openedSubmenu = id;
    },
    closeSubmenu({ relatedTarget } = {}) {
      if (!relatedTarget?.classList.contains('nav-sublink')) {
        this.openedSubmenu = null;
      }
    },
    toggleSubmenu(id) {
      if (this.openedSubmenu === id) {
        this.closeSubmenu();
      } else {
        this.openSubmenu(id);
      }
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false;
    },
    openMobileMenu() {
      this.isMobileMenuOpen = true;
    },
    applyOpacity(color, opacity) {
      const tColor = tinycolor(color);
      // must return with toString to work on server generated HTML
      return tColor.setAlpha(opacity).toString();
    },
    itemHasSubmenu(menuLink) {
      return menuLink.menuLinks?.length > 0;
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'header',
            label: 'Cabeçalho',
            position: 0,
            fields: [
              {
                name: 'logoImg',
                type: 'ImageFile',
                label: 'Logo',
                maxWidth: 300,
                maxHeight: 90,
                resizeInsteadCrop: true,
                helpText: 'Tamanho máximo 300x90, formato .PNG',
                enableRemoval: true,
              },
              {
                name: 'hasLogoLink',
                type: 'Checkbox',
                label: 'Adicionar link no logo',
              },
              {
                name: 'logoUrl',
                type: 'Url',
                label: 'Link do logo',
                isEnabled({ hasLogoLink }) {
                  return hasLogoLink;
                },
              },
              {
                name: 'logoAltText',
                type: 'ShortText',
                label: 'Texto alternativo',
                helpText:
                  'O texto será usado como descrição alternativa (Alt) para a imagem e aparecerá caso não haja um logo definido',
              },
              {
                name: 'menuLinks',
                type: 'MenuSelect',
                label: 'Tipo de menu',
                helpText:
                  'Abaixo você poderá escolher entre usar um menu customizado, exclusivo para este módulo, ou usar um menu global reutilizável através de módulos e páginas',
                customMenuLabel: 'Itens do menu',
                customMenuHelpText:
                  'Reorganize os itens do menu os arrastando. Crie um submenu movendo um item para dentro de outro.',
              },
              {
                name: 'linksPosition',
                type: 'VueSelect',
                label: 'Posição dos menus',
                default: 'Direita',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'Esquerda',
                  },
                  {
                    label: 'Direita',
                    value: 'Direita',
                  },
                  {
                    label: 'Centro',
                    value: 'Centro',
                  },
                ],
              },
              {
                name: 'buttons',
                type: 'List',
                maxItems: 1,
                label: 'Botões',
                itemLabel: 'Botão {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar botão',
                fields: [
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 30,
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                  },
                ],
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
      {
        name: 'appearance',
        label: 'Aparência',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'textColor',
                type: 'ColorPicker',
                label: 'Cor dos links',
                position: 0,
              },
              {
                name: 'buttonColor',
                label: 'Cor do botão',
              },
              {
                name: 'backgroundColor',
                after: 'buttonColor',
              },
              {
                name: 'buttonTextColor',
                type: 'ColorPicker',
                label: 'Cor de texto do botão',
              },
              {
                name: 'opacity',
                type: 'Range',
                label: 'Opacidade',
                min: 0,
                max: 1,
                step: 0.1,
                format: {
                  decimals: 1,
                },
              },
              {
                name: 'useGradient',
                type: 'Checkbox',
                label: 'Usar gradiente',
              },
            ],
            toRemove: ['titleColor', 'textColor', 'buttonTextColor'],
          },
          {
            name: 'fonts',
            fields: [
              {
                name: 'contentSizeDesk',
                label: 'Links desk',
              },
              {
                name: 'contentSizeMobile',
                label: 'Links mobile',
              },
              {
                name: 'buttonSizeDesk',
                type: 'Range',
                label: 'Botão desk',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
              {
                name: 'buttonSizeMobile',
                type: 'Range',
                label: 'Botão mobile',
                min: 8,
                max: 100,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
            toRemove: ['titleSizeDesk', 'titleSizeMobile'],
          },
        ],
        toRemove: ['paddingMobile', 'transitions'],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.menu {
  position: relative;
  background: transparent;
  min-height: 90px;
  width: 100%;

  &.transparent {
    position: absolute;
    left: 0;

    .header {
      z-index: 10;
    }
  }

  .header {
    position: relative;
    margin: 0;

    &__content {
      text-align: left;
      z-index: 10;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: calc(var(--wrapper) * 1.25);
      max-width: 95%;
      margin: 0 auto;
      padding: 0;
      padding-top: var(--padding-top);
      padding-bottom: var(--padding-bottom);
      min-height: 90px;

      > *:not(:first-child) {
        margin-left: 8px;

        @include media-breakpoint-up(lg) {
          margin-left: 32px;
        }
      }

      &.links-center {
        @include media-breakpoint-up(lg) {
          .logo-wrapper {
            margin: 0 !important;
          }
        }
      }

      &.links-left {
        @include media-breakpoint-up(lg) {
          .logo-wrapper {
            position: absolute;
            margin: 0 !important;
            left: 50%;
            transform: translate(-50%, 0%);
          }
        }
      }

      .logo-wrapper {
        margin-right: auto;

        .editor-link {
          color: inherit;

          img {
            max-width: 300px; // min() falback
            max-width: min(300px, 35vw, 100%);

            max-height: 90px;
            vertical-align: middle;
          }
        }

        h1,
        h2 {
          color: var(--text-color, $lb-white);
          font-weight: $lb-semibold;
          line-height: 1.2em;
          font-size: $size-small;

          @include media-breakpoint-up(lg) {
            font-size: 32px;
          }
        }
      }

      .hamburger-btn {
        padding: 8px;

        @include media-breakpoint-up(lg) {
          // this was done via css and not v-if/v-show to avoid flickering
          // since the server doesn't know the client screen width.
          display: none;
        }
      }
    }

    &__buttons {
      display: flex;

      .cta + .cta {
        margin-left: 8px;
      }

      .cta {
        color: var(--button-text-color, $lb-white);
        line-height: 1.25;
        font-size: var(--button-size-desk);

        @include media-breakpoint-down(lg) {
          font-size: var(--button-size-mobile);
        }

        @include media-breakpoint-down(sm) {
          padding: 8px 10px;
        }

        &:not(.vazado) {
          background-color: var(--button-color, var(--primary-color, $primary));
        }

        &.vazado {
          color: var(--text-color, $lb-white);
        }
      }
    }
  }

  nav {
    font-size: $size-small;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 80px 42px;
    overflow: auto;

    > ul {
      line-height: 1.5;
      text-align: center;

      > li {
        position: relative;
        color: var(--text-color, $lb-white);

        .nav-link {
          display: inline-block;
          font-size: 16px;
          font-weight: $lb-semibold;
          color: inherit;
          border-color: var(--primary-color, $crm-blue-primary);
          padding: 20px 0;

          &:hover,
          &:focus {
            color: inherit;
          }

          .icon {
            margin-left: 12px;
            transform: rotate(0);
            transition: transform 0.35s;
          }

          &.active {
            .icon {
              transform: rotate(180deg);
            }
          }
        }

        ul {
          position: relative;
          background: $lb-white;
          border-radius: 15px;
          box-shadow: 0 3px 3px rgba($lb-black, 0.25);
          font-size: $size-normal;
          padding: 20px 0;
          width: 210px;
          margin: 0 auto;

          &::after {
            content: '';
            height: 0;
            width: 0;
            position: absolute;
            bottom: calc(100% - 1px);
            left: 50%;
            margin-left: -11px;
            border: 11px solid transparent;
            border-bottom-color: $lb-white;
          }

          li:not(:last-of-type) {
            border-bottom: 1px solid $lb-ice;
          }

          .editor-link {
            color: $lb-dark-gray;
            display: block;
            padding: 10px 30px;
            transition: background-color 200ms ease-in-out;
            position: relative;
            line-height: 1;

            &:focus,
            &:hover {
              background: $lb-light-gray;
            }
          }
        }
      }
    }

    > button {
      position: fixed;
      z-index: 9;
      right: 6px;
      top: 6px;
      padding: 10px;
    }

    .header__buttons {
      font-size: var(--content-size-desk, 16px);
      .cta {
        font-size: 1em;
      }
    }
  }

  .nav-mobile {
    display: none;
    background: var(--primary-color, $crm-blue-primary);
    > ul {
      > li {
        color: var(--mobile-text-color, $lb-white);

        .nav-link {
          font-size: var(--content-size-mobile);
        }
      }
    }

    &[is-open] {
      display: block;
      margin: 0;
      z-index: 9999;
    }
  }

  .nav-desk {
    display: none;
    background: transparent;
    position: relative;
    padding: 0;
    width: auto;
    height: auto;
    overflow: unset;

    @include media-breakpoint-up(lg) {
      display: block;
    }

    > ul {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      margin: 0 -16px;

      > li {
        display: flex;
        margin: 0 16px;

        &::after {
          content: '';
          display: block;
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          height: 2px;
          transform: scaleX(0);
          transition: transform 200ms ease-in-out;
          border-bottom-width: 2px;
          border-bottom-style: solid;
          border-bottom-color: inherit;
        }

        &:hover::after {
          transform: scaleX(1);
        }

        .nav-link {
          font-size: var(--content-size-desk);
          padding: 8px 0;
          margin: auto;
        }

        ul {
          position: absolute;
          z-index: 5010;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);

          &.slide-down-enter,
          &.slide-down-leave-to {
            transform: translateX(-50%) scaleY(0);
          }
        }
      }
    }
  }

  .slide-in-enter-active,
  .slide-in-leave-active,
  .slide-down-enter-active,
  .slide-down-leave-active {
    overflow: hidden;
    transform-origin: top;
    transition: transform 0.25s ease-in-out, opacity 0.35s ease-out;
  }

  .slide-in-enter-active,
  .slide-in-leave-active {
    transform-origin: right;
  }

  .slide-down-enter-active,
  .slide-down-leave-active {
    transform-origin: top;
  }

  .slide-in-enter,
  .slide-in-leave-to,
  .slide-down-enter,
  .slide-down-leave-to {
    opacity: 0;
  }

  .slide-in-enter,
  .slide-in-leave-to {
    transform: scaleX(0);
  }

  .slide-down-enter,
  .slide-down-leave-to {
    transform: scaleY(0);
  }
}
</style>
